import type { RiskLevel } from './types'

/**
 * How a risk level LOOKS on a vCRO screen. Never which level a number is: the
 * level always arrives from the API (the one risk engine bands every score;
 * apps/api/tests/unit/recipients/risk-engine-one-definition.guard.spec.ts).
 *
 * The colours follow the workspace's theme in light and dark. The two ends of
 * the ramp are the theme's own `--success` and `--destructive` (every
 * ready-made theme sets both); the middle two are a fixed amber and orange,
 * lighter in dark mode. `VCRO_LEVEL_TOKENS` defines them as CSS variables on
 * a vCRO page's root, so a chart, a pill and a sparkline all read one value.
 */

/** Most severe first: the order of the People list's level chips. */
export const LEVELS_MOST_SEVERE_FIRST = ['Critical', 'High', 'Medium', 'Low', 'None'] as const satisfies readonly RiskLevel[]

/** The four measured levels, lowest first: the order of a gauge's arcs and a trend's bands. */
export const MEASURED_LEVELS_LOWEST_FIRST = ['Low', 'Medium', 'High', 'Critical'] as const satisfies readonly RiskLevel[]
export type MeasuredLevel = (typeof MEASURED_LEVELS_LOWEST_FIRST)[number]

/** Put on the root of every vCRO page (the class carries the five colour variables). */
export const VCRO_LEVEL_TOKENS = [
  '[--vcro-level-none:color-mix(in_oklab,var(--muted-foreground)_45%,transparent)]',
  '[--vcro-level-low:var(--success)]',
  '[--vcro-level-medium:oklch(0.78_0.15_78)]',
  'dark:[--vcro-level-medium:oklch(0.82_0.14_82)]',
  '[--vcro-level-high:oklch(0.68_0.18_47)]',
  'dark:[--vcro-level-high:oklch(0.74_0.16_50)]',
  '[--vcro-level-critical:var(--destructive)]',
  // Wave 2: the diverging 0..100 scale around the engine's neutral 50 (lib/vcro/org.ts heatCellBackground).
  '[--vcro-heat-lowers:var(--vcro-level-low)]',
  '[--vcro-heat-raises:var(--vcro-level-critical)]',
  '[--vcro-heat-neutral:var(--muted)]',
].join(' ')

/** The level's colour, as a CSS value (for SVG fills and inline styles). */
export const LEVEL_COLOR: Record<RiskLevel, string> = {
  None: 'var(--vcro-level-none)',
  Low: 'var(--vcro-level-low)',
  Medium: 'var(--vcro-level-medium)',
  High: 'var(--vcro-level-high)',
  Critical: 'var(--vcro-level-critical)',
}

/** A pill: a tint of the level's colour, its text the colour mixed toward the foreground for contrast. */
export const LEVEL_PILL_CLASS: Record<RiskLevel, string> = {
  None: 'border-dashed border-border bg-transparent text-muted-foreground',
  Low: 'border-transparent bg-[color-mix(in_oklab,var(--vcro-level-low)_16%,transparent)] text-[color-mix(in_oklab,var(--vcro-level-low)_55%,var(--foreground))]',
  Medium:
    'border-transparent bg-[color-mix(in_oklab,var(--vcro-level-medium)_22%,transparent)] text-[color-mix(in_oklab,var(--vcro-level-medium)_45%,var(--foreground))]',
  High: 'border-transparent bg-[color-mix(in_oklab,var(--vcro-level-high)_18%,transparent)] text-[color-mix(in_oklab,var(--vcro-level-high)_55%,var(--foreground))]',
  Critical:
    'border-transparent bg-[color-mix(in_oklab,var(--vcro-level-critical)_16%,transparent)] text-[color-mix(in_oklab,var(--vcro-level-critical)_60%,var(--foreground))]',
}

/** Colour of a change that RAISES the score (worse) and one that LOWERS it (better). */
export const RAISES_COLOR = 'var(--vcro-level-high)'
export const LOWERS_COLOR = 'var(--vcro-level-low)'
export const RAISES_TEXT_CLASS = 'text-[color-mix(in_oklab,var(--vcro-level-high)_70%,var(--foreground))]'
export const LOWERS_TEXT_CLASS = 'text-[color-mix(in_oklab,var(--vcro-level-low)_70%,var(--foreground))]'
